import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { CustomerProfile } from '@/types/customer';
import useCustomerName from '@/composables/useCustomerName';
import useCustomerAdmission from '@/composables/useCustomerAdmission';
import useImpersonateData from '@/composables/useImpersonateData.ts';

export const getEmptyCustomerProfile = (): CustomerProfile => ({
  fullName: '',
  firstName: '',
  lastName: '',
  fileNumber: '',
  admission: null,
  homeAddress: null,
  homePhone: null,
  cellPhone: null,
  email: '',
  areAddressesRestricted: false,
  assistiveDevices: [],
  bookingCompanionsAllowed: true,
  otherEmailAddresses: [],
  isLavalCustomer: false,
  isBookingAllowed: true,
  administrativeNote: null,
  disability: null,
});

/**
 * Informations relative à l'utilisateur connecté
 */
const useCustomerStore = defineStore('customerProfile', () => {
  const customer = ref<CustomerProfile>(getEmptyCustomerProfile());
  const { setFullName, setFirstAndLastName } = useCustomerName();
  const { setImpersonateData } = useImpersonateData();
  const { setAdmission } = useCustomerAdmission();

  /** 'utilisateur ne peut se déplacer qu'à une liste restreinte d'adresses */
  const addressesRestricted = computed<boolean>(() => customer.value.areAddressesRestricted);

  function setCustomer(data: CustomerProfile) {
    customer.value = data;
    setFullName(data.fullName);
    setFirstAndLastName(data.firstName, data.lastName);
    setAdmission(data.admission);
    setImpersonateData(data);
  }

  function resetCustomer() {
    customer.value = getEmptyCustomerProfile();
    setAdmission(null);
    setFullName('');
    setImpersonateData(customer.value);
  }

  /** Adresse de domicile de l'utilisateur */
  const homeAddress = computed(() => customer.value.homeAddress);

  return {
    customer,
    homeAddress,
    addressesRestricted,
    setCustomer,
    resetCustomer,
  };

});

export default useCustomerStore;
